/**
 * JSON backup export/import (PLAN §9).
 *
 * The file is the persisted state verbatim — progress and settings — which is
 * the same shape the future iOS app reads. Moving a backup between devices is
 * the sync story; there is no server.
 *
 * Import is destructive in a way the Letterboxd import is not: a Letterboxd
 * file merges, a backup *restores*, replacing progress and settings wholesale.
 * So it states the count it is about to replace and requires a second click.
 */

import { useEffect, useRef, useState } from "react";
import {
  type BackupSummary,
  backupFilename,
  readBackup,
  serializeBackup,
} from "@/lib/backup/index.ts";
import { readPersistedState } from "@/state/persistedState.ts";
import { useProgressStore } from "@/state/progress.ts";
import { useSettingsStore } from "@/state/settings.ts";
import styles from "./BackupDialog.module.css";

function todayISO(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

export function BackupDialog() {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<BackupSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const watchedCount = useProgressStore((s) => Object.keys(s.watched).length);
  const applyImport = useProgressStore((s) => s.applyImport);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const close = () => {
    setOpen(false);
    setPending(null);
    setError(null);
  };

  const download = () => {
    // Read straight from storage rather than reassembling from the stores, so
    // the file is exactly what the app persists — including any settings a
    // future build adds that this component doesn't know about.
    const json = serializeBackup(readPersistedState());
    const blob = new Blob([json], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = backupFilename(todayISO());
    link.click();
    URL.revokeObjectURL(url);
  };

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setPending(null);
    try {
      setPending(readBackup(await file.text()));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const restore = () => {
    if (!pending) return;
    // "replace" is the point of a restore: the backup becomes the whole truth,
    // not a merge on top of whatever is already here.
    applyImport(pending.state.watched, "replace");
    const { encoreMode, hoursPerWeek, themeSource, alternativesEnabled } = pending.state.settings;
    useSettingsStore.setState({ encoreMode, hoursPerWeek, themeSource, alternativesEnabled });
    close();
  };

  return (
    <>
      <button type="button" className={styles.trigger} onClick={() => setOpen(true)}>
        Backup
      </button>

      <dialog ref={dialogRef} className={styles.dialog} onClose={close}>
        <div className={styles.head}>
          <h2 className={styles.title}>Backup &amp; restore</h2>
          <button type="button" className={styles.close} onClick={close} aria-label="Close">
            Done
          </button>
        </div>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Export</h3>
          <p className={styles.note}>
            Your {watchedCount} watched {watchedCount === 1 ? "stop" : "stops"} and your settings,
            as one JSON file. Nothing leaves this browser unless you move the file yourself, and
            it's the same format the iOS app will read.
          </p>
          <button type="button" className={styles.primary} onClick={download}>
            Download backup
          </button>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Restore</h3>
          <p className={styles.note}>
            Restoring <strong>replaces</strong> your current progress and settings. It doesn't merge
            For merging a watch history, use Import from Letterboxd instead.
          </p>

          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className={styles.file}
            onChange={(event) => void choose(event.target.files?.[0])}
          />

          {error && (
            <p className={styles.error} role="alert">
              That file couldn't be read: {error}
            </p>
          )}

          {pending && (
            <div className={styles.confirm}>
              <p>
                This backup holds <strong>{pending.watchedCount}</strong>{" "}
                {pending.watchedCount === 1 ? "watched stop" : "watched stops"}
                {pending.migrated && ` (written by an older version, v${pending.version})`}. It will
                replace the {watchedCount} you have now.
              </p>
              <button type="button" className={styles.danger} onClick={restore}>
                Replace my progress
              </button>
            </div>
          )}
        </section>
      </dialog>
    </>
  );
}
